import React, { useEffect, useState } from "react";
import { Button, Space, Typography, Card, message, Tooltip } from "antd";
import { FaFileExport, FaPlus, FaUserAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import CustomerTable from "../../features/CustomerManagment/CustomerTable";
import CustomerFilters from "../../features/CustomerManagment/CustomerFilters";
import CustomerDetailModal from "../../features/CustomerManagment/CustomerDetailModal";
import TagManagementModal from "../../features/CustomerManagment/TagManagmentModal";
import EmailModal from "../../features/CustomerManagment/EmailModal";
import ExportModal from "../../features/CustomerManagment/ExportModal";
import { statusOptions, tagOptions } from "../../utils/constants";
import {
  error_toast_message,
  success_toast_message,
} from "../../utils/toast_message";
import {
  getAllCustomers,
  updateCustomerStatus,
  updateCustomerTags,
} from "../../services/CustomerManagment";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../utils/asyncStatus";
import { setIdleStatus } from "../../store/slice/CustomerManagemetSlice";

const { Title, Text } = Typography;

const CustomerManagement = () => {
  const [search, setSearch] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [customerDetailModalVisible, setCustomerDetailModalVisible] =
    useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange] = useState([]);
  const [exportVisible, setExportVisible] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [tagModalVisible, setTagModalVisible] = useState(false);

  const {
    get_all_customers_data,
    get_all_customers_status,
    update_customer_tags_status,
    update_customer_status_status,
  } = useSelector((state) => state.customerManagement);

  const customersLoader = get_all_customers_status === asyncStatus.LOADING;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (
      update_customer_tags_status === asyncStatus.SUCCEEDED ||
      update_customer_status_status === asyncStatus.SUCCEEDED
    ) {
      dispatch(getAllCustomers());
      dispatch(setIdleStatus());
    }
  }, [update_customer_tags_status, update_customer_status_status, dispatch]);


  const filterCustomers = (customers) => {
    if (!customers) return [];

    return customers.filter((customer) => {
      // Get phone number from either shipping_address or orders' customer_info
      let customerPhone = "";

      // Try to get phone from shipping_address first
      if (customer.shipping_address && customer.shipping_address.length > 0) {
        customerPhone = customer.shipping_address[0].phone || "";
      }

      // If not found in shipping_address, try to get from first order's customer_info
      if (
        !customerPhone &&
        customer.orders &&
        customer.orders.length > 0 &&
        customer.orders[0].customer_info
      ) {
        customerPhone = customer.orders[0].customer_info.phone || "";
      }

      const searchMatch =
        search === "" ||
        `${customer.first_name} ${customer.last_name}`
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        customer.email.toLowerCase().includes(search.toLowerCase()) ||
        (customerPhone && customerPhone.includes(search));

      const statusMatch =
        statusFilter === "All" || customer.status === statusFilter;

      let dateMatch = true;
      if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        const customerDate = moment(customer.createdAt);
        dateMatch =
          customerDate.isSameOrAfter(dateRange[0], "day") &&
          customerDate.isSameOrBefore(dateRange[1], "day");
      }

      return searchMatch && statusMatch && dateMatch;
    });
  };

  const filteredCustomers = filterCustomers(get_all_customers_data);

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailModalVisible(true);
  };

  const handleManageTags = (customer) => {
    setSelectedCustomer(customer);
    setTagModalVisible(true);
  };

  const handleSendEmail = (customer) => {
    setSelectedCustomer(customer);
    setEmailModalVisible(true);
  };

  const handleSubmitEmail = () => {
    success_toast_message(`Email sent to ${selectedCustomer.email}`);
    setEmailModalVisible(false);
  };

  const handleExportCustomers = () => {
    success_toast_message("Customer data exported to CSV");
    setExportVisible(false);
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setDateRange([]);
  };

  const handleStatusChange = async (customer, newStatus) => {
    try {
      await dispatch(
        updateCustomerStatus({
          id: customer._id,
          status: newStatus,
        })
      ).unwrap();

      success_toast_message(`Customer status updated to ${newStatus}`);
    } catch (error) {
      error_toast_message("Failed to update customer status");
      console.error("Status update error:", error);
    }
  };

  // Tags update handler
  const handleSubmitTags = async (tags) => {
    if (!selectedCustomer) return;

    console.log(tags ,"tags ")
    try {
      await dispatch(
        updateCustomerTags({
          id: selectedCustomer._id,
          tags: tags,
        })
      ).unwrap();

      success_toast_message("Tags updated successfully");
      setTagModalVisible(false);
    } catch (error) {
      error_toast_message("Failed to update tags");
      console.error("Tags update error:", error);
    }
  };

    return (
      <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg max-2xl:max-w-[1000px] max-xl:max-w-[850px]   ">
        <div className="flex justify-between items-center mb-6">
          <Title level={3} className="m-0">
            Customer Management
          </Title>
          <Space>
            <Tooltip title="Export Customers">
              <Button
                icon={<FaFileExport />}
                onClick={() => setExportVisible(true)}
              >
                Export
              </Button>
            </Tooltip>
          </Space>
        </div>

        <CustomerFilters
          search={search}
          statusFilter={statusFilter}
          dateRange={dateRange}
          statusOptions={statusOptions}
          filterVisible={filterVisible}
          onSearchChange={(e) => setSearch(e.target.value)}
          onStatusFilterChange={setStatusFilter}
          onDateRangeChange={setDateRange}
          onFilterToggle={() => setFilterVisible(!filterVisible)}
          onResetFilters={handleResetFilters}
        />

        <CustomerTable
          customers={filteredCustomers}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          onViewCustomer={handleViewCustomer}
          onManageTags={handleManageTags}
          onSendEmail={handleSendEmail}
          onStatusChange={handleStatusChange}
          loaders={customersLoader}
        />

        <CustomerDetailModal
          visible={customerDetailModalVisible}
          customer={selectedCustomer}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          onClose={() => setCustomerDetailModalVisible(false)}
        />

        <TagManagementModal
          visible={tagModalVisible}
          customer={selectedCustomer}
          tagOptions={tagOptions}
          onCancel={() => setTagModalVisible(false)}
          onSaveTags={handleSubmitTags}
        />

        <EmailModal
          visible={emailModalVisible}
          customer={selectedCustomer}
          onCancel={() => setEmailModalVisible(false)}
          onSendEmail={handleSubmitEmail}
        />

        <ExportModal
          visible={exportVisible}
          customers={get_all_customers_data || []}
          statusOptions={statusOptions}
          onCancel={() => setExportVisible(false)}
        />
      </div>
    );
  };

export default CustomerManagement;
